import React, { useState, useRef } from 'react';
import { Box, Card, CardContent, CardMedia, Typography, Grid, Paper, Slider, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { kutfok } from '../../Data/kutfokData';
import placeholder from '../../Assets/Kutfok/placeholder-kutfo.png';

function KnowledgeBase() {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const itemRefs = useRef(kutfok.map(() => React.createRef()));

    const numericYears = kutfok.map(item => item.year).filter(y => !isNaN(y));

    const [selectedYear, setSelectedYear] = useState(numericYears[0] || 1283);

    const handleSliderChange = (event, newValue) => {
        if (typeof newValue === 'number') {
            setSelectedYear(newValue);
            scrollToNearestItem(newValue);
        }
    };

    const scrollToNearestItem = (year) => {
        const nearestIndex = kutfok.reduce((prevIndex, curr, currIndex) => {
            const prevDiff = Math.abs(kutfok[prevIndex].year - year);
            const currDiff = Math.abs(curr.year - year);
            return prevDiff < currDiff ? prevIndex : currIndex;
        }, 0);

        if (itemRefs.current[nearestIndex]?.current) {
            itemRefs.current[nearestIndex].current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    // Slider mark-ek 50 évente, kerek számok
    const sliderMarks = [];
    for (let year = 1250; year <= 2000; year += 50) {
        sliderMarks.push({
            value: year,
            label: <Typography style={{ color: '#fff', fontSize: isMobile ? '0.7rem' : '1rem' }}>{year}</Typography>
        });
    }

    return (
        <Box sx={{ mt: '50px' }}>
            <Typography variant={isMobile ? "h5" : "h4"} style={{ textAlign: "center", paddingTop: "1rem", paddingBottom: "1rem",  background: "#032b3b", color: "#fff" }}>
                {t('KnowledgeBase.Title')}
            </Typography>
            <Typography variant="h6" style={{ textAlign: "center", paddingTop: ".5rem", paddingBottom: ".5rem", marginBottom: "0rem", background: "#032b3b", color: "#fff", fontSize: isMobile ? '1rem' : '1.25rem' }}>
                {t('KnowledgeBase.ReferenceCount', { count: kutfok.length })}
            </Typography>

            {/* MUI Slider */}
            <Grid container justifyContent='center' style={{background: "#032b3b", padding: isMobile ? '0 0 1rem 0' : '0'}}>
                <Slider
                    value={selectedYear}
                    onChange={handleSliderChange}
                    min={Math.min(...numericYears)}
                    max={Math.max(...numericYears)}
                    step={1}
                    style={{ 
                        height: isMobile ? '0.2rem' : '0.3rem', 
                        width: isMobile ? '90%' : '80%', 
                        margin: isMobile ? '0.5rem 0 1.5rem 0' : '2rem', 
                        background: "#fff"
                    }}
                    marks={isMobile ? sliderMarks.filter((_, i) => i % 2 === 0) : sliderMarks}
                />
            </Grid>

            {/* Scrollable container */}
            <div style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                <Grid container spacing={0} style={{ background: "#032b3b", padding: isMobile ? '0.5rem' : '1rem' }}>
                    {kutfok.map((item, index) => (
                        <Grid container spacing={isMobile ? 1 : 2} key={index} ref={itemRefs.current[index]} style={{ marginBottom: isMobile ? '1rem' : '0.5rem' }}>
                            {/* Date / timeline */}
                            <Grid item xs={12} md={3} style={{ 
                                display: "flex", 
                                alignItems: "center", 
                                justifyContent: isMobile ? "flex-start" : "flex-end", 
                                borderRight: isMobile ? "none" : "2px solid white", 
                                borderBottom: isMobile ? "1px solid rgba(255,255,255,0.3)" : "none",
                                position: 'relative',
                                paddingLeft: isMobile ? '1.5rem' : '0',
                                marginBottom: isMobile ? '5px' : '0'
                            }}>
                                <Typography variant="body1" gutterBottom style={{ textAlign: isMobile ? "left" : "right", color: "white", fontWeight: 'bold' }}>
                                    {item.year}
                                    {!isMobile && <span style={{ marginLeft: "5px", fontSize: '1.5rem' }}>-</span>}
                                </Typography>
                            </Grid>

                            {/* Card */}
                            <Grid item xs={12} md={9} xl={4} style={{ margin: isMobile ? "0.3rem" : ".7rem" }}>
                                <Paper elevation={3} style={{ padding: isMobile ? '5px' : '10px', height: '100%' }}>
                                    <Card style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', height: '100%' }}>
                                        <CardMedia
                                            component="img"
                                            image={item.coverimage || placeholder}
                                            alt={item.title}
                                            style={{ 
                                                maxWidth: isMobile ? '100%' : '100px', 
                                                maxHeight: isMobile ? '250px' : '20vh', 
                                                marginRight: isMobile ? '0' : '10px',
                                                marginBottom: isMobile ? '10px' : '0',
                                                objectFit: 'contain'
                                            }}
                                        />
                                        <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                            <CardContent style={{ flexGrow: 1, padding: isMobile ? '8px' : '16px' }}>
                                                <Typography variant={isMobile ? "h6" : "h5"} component="div" style={{ fontWeight: 'bold', fontSize: isMobile ? '1.1rem' : '1.5rem' }}>
                                                    {item.title}
                                                </Typography>
                                                <Typography color="textSecondary" gutterBottom style={{ color: '#8a8f94', fontSize: isMobile ? '0.8rem' : '1rem' }}>
                                                    {item.author}
                                                </Typography>
                                                <Typography variant="body2" component="p" style={{  whiteSpace: 'pre-line', fontSize: isMobile ? '0.85rem' : '1rem' }}>
                                                    {item.content}
                                                </Typography>
                                            </CardContent>
                                            {/* A link mindig a kártya alján */}
                                            {item.link && (
                                                <div style={{ marginTop: 'auto', padding: '0 8px 8px 8px' }}>
                                                    <a
                                                        href={item.link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ color: '#00bfff', textDecoration: 'underline', fontWeight: 'bold', fontSize: isMobile ? '0.8rem' : '1rem' }}
                                                    >
                                                        {t('KnowledgeBase.ReadMore')}
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </Card>

                                </Paper>
                            </Grid>
                        </Grid>
                    ))}
                </Grid>
            </div>
        </Box>
    );
}

export default KnowledgeBase;
